import { EncodedStream } from '@fullstory/recording-api';

interface Node {
  idx: number;
  map: { [key: string]: Node };
}

/**
 * Compression of our raw event stream.
 */
export interface Encoder {
  encode(data: string[]): EncodedStream;
}

/**
 * Encoder that returns the given input unchanged.
 * Note: this can be paired with standard decoder.
 */
export class IdentityEncoder implements Encoder {
  encode(data: string[]): EncodedStream {
    return data;
  }
}

/**
 * LZW encoder for lossless data compression
 * wiki ref: https://en.wikipedia.org/wiki/Lempel–Ziv–Welch
 */
export class LZJGWEncoder implements Encoder {
  private static MAX_NODES = 10000;

  private dict: Node = { idx: -1, map: {} };
  private nodeCount = 1;
  private startIdx = 0;

  encode(data: string[]): EncodedStream {
    if (data.length == 0) {
      return [];
    }

    let previous = data[0] as string;
    let idx;
    let node: Node | undefined = Object.prototype.hasOwnProperty.call(this.dict.map, previous)
      ? this.dict.map[previous]
      : undefined;
    const out: EncodedStream = [];
    let currChar: string;
    let phraseLen = 1;

    function dictRef() {
      if (node) {
        if (phraseLen > 1) {
          out.push([node.idx, phraseLen]);
        } else {
          out.push(node.idx);
        }
      } else {
        out.push(previous);
      }
    }

    for (idx = 1; idx < data.length; idx++) {
      currChar = data[idx] as string;
      if (node && Object.prototype.hasOwnProperty.call(node.map, currChar)) {
        phraseLen++;
        previous = currChar;
        node = node.map[currChar];
      } else {
        dictRef();
        const newidx = this.startIdx + idx - phraseLen;
        if (node == null && this.nodeCount < LZJGWEncoder.MAX_NODES) {
          node = { idx: newidx, map: {} };
          this.dict.map[previous] = node;
          this.nodeCount++;
        }
        if (node && this.nodeCount < LZJGWEncoder.MAX_NODES) {
          node.map[currChar] = { idx: newidx, map: {} };
          this.nodeCount++;
        }
        phraseLen = 1;
        previous = currChar;
        node = Object.prototype.hasOwnProperty.call(this.dict.map, currChar)
          ? this.dict.map[currChar]
          : undefined;
      }
    }
    dictRef();

    this.startIdx += data.length;
    return out;
  }
}
